<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot></slot>
      </div>
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between">
        {{$t('routes.pod.requestFilesLink')}}
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="tab" align-tabs="center" show-arrows>
        <v-tab>{{$t('menus.menuUploadLink.uploadLinkInformation')}}</v-tab>
        <v-tab>{{$t('menus.menuUploadLink.emailInvitations')}}</v-tab>
      </v-tabs>

      <v-card-text ref="cardText" class="scrollable">
        <v-form ref="form" v-on:submit.prevent="submitForm" autocomplete="off" :disabled="isFormDisabled">
          <v-window v-model="tab" style="background-color: unset !important;">
            <v-window-item class="pt-4">
              <div class="mb-4">
                {{$t('menus.menuUploadLink.destinationFolderInfo')}}

                <SelectNodeDialog
                  v-model="selectedNodeId"
                  v-slot:default="slotProps"
                >
                  <v-btn
                    variant="flat" block
                    color="info">
                      <span v-if="slotProps && slotProps.selectedNode">
                        {{ slotProps.selectedNode.name }}
                      </span>
                  </v-btn>
                </SelectNodeDialog>
              </div>

              <div>
                {{ $t('menus.menuUploadLink.password') }}:
                <v-text-field
                  type="text"
                  :placeholder="$t('menus.menuUploadLink.password')"
                  :aria-label="$t('menus.menuUploadLink.password')"
                  v-model="password"
                  v-bind:rules="passwordRules"
                  @update:model-value="$refs.form.validate()"
                  required
                  block
                  variant="solo-filled"
                  flat
                  data-cy="tools-uploadLink-password"
                >
                  <template v-slot:append-inner>
                    <v-tooltip v-if="canAccessClipboard && password && password.length > 0" location="bottom" :z-index="1200" :aria-label="$t('menus.menuUploadLink.copyPassword')">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          v-on:click="copyPasswordToClipboard"
                          :aria-label="$t('menus.menuUploadLink.copyPassword')"
                          variant="text"
                          width="36"
                          height="36"
                          icon
                        >
                          <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                      </template>

                      <span>{{ $t('menus.menuUploadLink.copyPassword') }}</span>
                    </v-tooltip>

                    <v-tooltip location="bottom" :z-index="1200" :aria-label="$t('menus.menuUploadLink.generatePassword')">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          v-on:click="generatePassword"
                          :aria-label="$t('menus.menuUploadLink.generatePassword')"
                          variant="text"
                          width="36"
                          height="36"
                          icon
                        >
                          <v-icon>mdi-lock-reset</v-icon>
                        </v-btn>
                      </template>

                      <span>{{ $t('menus.menuUploadLink.generatePassword') }}</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </div>

              <div>
                {{$t('menus.menuUploadLink.expiryDate')}}:
                <v-menu
                  v-model="datepickerMenu"
                  v-bind:close-on-content-click="false"
                  v-bind:offset="40"
                  transition="scale-transition"
                  min-width="auto"
                >
                  <template v-slot:activator="{ props }">
                    <v-text-field
                      v-model="expiryDate"
                      :placeholder="$t('menus.menuUploadLink.expiryDate')"
                      :aria-label="$t('menus.menuUploadLink.expiryDate')"
                      v-bind:rules="expiryDateRules"
                      append-inner-icon="mdi-calendar"
                      readonly
                      required
                      block
                      variant="solo-filled"
                      flat
                      v-bind="props"
                      data-cy="tools-uploadLink-dateMenu"
                    />
                  </template>

                  <v-date-picker
                    v-model="expiryDate"
                    @update:model-value="datepickerMenu = false"
                    v-bind:min="getMinDatepicker"
                    data-cy="tools-uploadLink-datePicker"
                  />
                </v-menu>
              </div>

              <div class="d-flex">
                <div class="flex-grow-1">
                  {{$t('menus.menuUploadLink.maxFilesize')}}:
                  <v-text-field
                    v-model="maxfilesize"
                    :placeholder="$t('menus.menuUploadLink.maxFilesize')"
                    :rules="maxfilesizeRules"
                    type="number"
                    required
                    block
                    variant="solo-filled"
                    flat
                    min="0"
                    data-cy="tools-uploadLink-maxFileSize"
                  />
                </div>

                <div class="ml-4 flex-grow-0">
                  &nbsp;
                  <v-select
                    v-model="maxfilesizeunit"
                    :items="availableFilesizeUnits"
                    flat
                    variant="solo-filled"
                  />
                </div>
              </div>

              <div>
                {{ $t('menus.menuUploadLink.notes') }}:
                <v-textarea
                  v-model="linkNotes"
                  :placeholder="$t('menus.menuUploadLink.notes')"
                  :aria-label="$t('menus.menuUploadLink.notes')"
                  variant="solo-filled"
                  flat
                  rows="3"
                  class="media-list-notes-textarea"
                  data-cy="tools-uploadLink-notes"
                />
              </div>

              <v-checkbox
                v-if="isTeamMemberUser"
                class="mt-0 pt-0"
                v-model="teamOnly"
                v-bind:label="$t('menus.menuUploadLink.teamOnly')"
                density="compact"
                hide-details
                required
                block
              />

              <v-checkbox
                class=""
                v-model="embeddable"
                v-bind:label="$t('menus.menuUploadLink.asEmbeddableWidget')"
                v-bind:rules="embeddableRules"
                v-bind:error-messages="password && embeddable ? $t('menus.menuUploadLink.cannotCreateEmbeddable') : ''"
                density="compact"
                required
                block
              />
            </v-window-item>

            <v-window-item class="pt-4">
              <div>
                <v-form ref="emailsForm" class="mt-4" @submit.prevent="addEmailRecipient">
                  <v-text-field
                    v-model="addEmailInput"
                    :rules="addEmailInputRules"
                    :placeholder="$t('common.emailAddress')"
                    :hint="$t('routes.pod.menuAccesslink.enterEmailAddressAndPress')"
                    variant="solo-filled"
                    class="mb-2"
                    flat
                  >
                    <template v-slot:append-inner>
                      <v-btn
                        size="small"
                        height="32"
                        density="default"
                        variant="elevated"
                        v-show="addEmailInput"
                        @click="addEmailRecipient"
                        prepend-icon="mdi-plus-circle"
                      >
                        {{$t('common.add')}}
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-form>

                <div v-if="emailRecipientsList.length > 0" class="vb-invited-recipients">
                  <v-chip
                    v-for="email in emailRecipientsList"
                    @click:close="removeEmailRecipient(email)"
                    color="primary"
                    :key="email"
                    closable
                    variant="flat"
                    close-icon="mdi-close"
                  >
                    {{ email }}
                  </v-chip>
                </div>
              </div>

              <div>
                <v-textarea
                  v-model="emailNote"
                  :placeholder="$t('menus.menuUploadLink.noteToEmail')"
                  :hint="$t('menus.menuUploadLink.noteToEmailHint')"
                  variant="solo-filled"
                  flat
                  rows="3"
                  class="mt-3"
                />
              </div>
              <div>
                <v-checkbox
                  v-model="includeQrCode"
                  :label="$t('routes.pod.menuAccesslink.addQrCodeToEmail')"
                  hide-details
                />
              </div>
              <div v-if="updatedLink?.invitations && Array.isArray(updatedLink.invitations) && updatedLink.invitations.length > 0" class="mt-4">
                <span class="font-weight-bold">{{ $t('routes.pod.menuAccesslink.invitedPeople') }}:</span> {{ updatedLink.invitations.map((i) => i.email).join(', ') }}
              </div>
            </v-window-item>
          </v-window>
        </v-form>

        <div v-if="updatedLink">
          <!-- Embed link code -->
          <div v-if="updatedLink?.params?.embeddable" class="d-flex">
            <div class="d-flex flex-column flex-grow-1">
              <div class="my-2">{{ $t('routes.node.code') }}:</div>
              <div>
                <v-textarea
                  variant="outlined"
                  readonly
                  :model-value="getEmbedlinkCode"
                />
              </div>
            </div>
            <div v-if="canAccessClipboard" class="flex-grow-0 pt-6 pl-2 align-content-center">
              <v-tooltip location="bottom" :z-index="1200" :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    type="button"
                    v-on:click="copyEmbeddedCodeToClipboard()"
                    variant="text"
                    icon
                    data-cy="podMenu-tools-embedMediaPreview-copyUrl-btn"
                  >
                    <v-icon>mdi-clipboard-file-outline</v-icon>
                  </v-btn>
                </template>

                {{ $t('routes.pod.menuEmbedMedia.copyToClipboard') }}
              </v-tooltip>
            </div>
          </div>

          <!-- Request link URL -->
          <div class="d-flex">
            <div class="flex-grow-1">
              <div class="my-2">{{ $t('routes.node.URL') }}:</div>
              <v-text-field
                variant="outlined"
                readonly
                hide-details="auto"
                :model-value="getUploadlinkURL"
              />
            </div>
            <div v-if="canAccessClipboard" class="flex-grow-0 pt-6 pl-2 align-content-end">
              <v-tooltip location="bottom" :z-index="1200" :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    type="button"
                    v-on:click="copyUrlToClipboard()"
                    variant="text"
                    icon
                    data-cy="podMenu-tools-embedMediaPreview-copyUrl-btn"
                  >
                    <v-icon>mdi-clipboard-file-outline</v-icon>
                  </v-btn>
                </template>

                {{ $t('routes.pod.menuEmbedMedia.copyToClipboard') }}
              </v-tooltip>
            </div>
          </div>
        </div>

        <v-row>
          <v-col cols="12">
            <div v-show="successMessage" class="bg-success pa-1 mt-1" data-cy="------">{{ successMessage }}</div>
            <div v-show="errorMessage" class="bg-error pa-1 mt-1" data-cy="------">{{ errorMessage }}</div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          @click="submitForm"
          :disabled="isFormDisabled"
          color="primary"
          variant="flat"
          data-cy="dialog--ok"
        >
          {{$t('common.create')}}
        </v-btn>
        <v-btn @click="dialog = false" variant="text">
          {{ updatedLink ? $t('common.close') : $t('common.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useNodeStore } from '@/stores/NodeStore.js';
import { useTenantStore } from '@/stores/TenantStore.js';
import { useUploadLinkStore } from '@/stores/UploadLinkStore.js';
import Helper from '@/components/helper.js';
import SelectNodeDialog from "../../../console/users/components/dialogs/SelectNodeDialog.vue";
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { UTCDate } from '@date-fns/utc';
import { formatDate, formatDateTimeMilliseconds } from '@/components/formatters.js';
import { addDays } from 'date-fns/addDays';
import { set } from 'date-fns/set';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "RequestFilesDialog",
  components: {
    SelectNodeDialog,
  },
  data() {
    const self = this;

    return {
      tab: 0,
      password: "",
      passwordRules: [
        function(v) {
          const value = v.trim();

          if (value && value.length < 8) {
            return this.$t('menus.createUser.passwordLength');
          }

          if (value && value.length > 191) {
            return this.$t('routes.pod.menuAccesslink.passwordMaxLength');
          }

          if (value && !Helper.isValidPassword(value)) {
            return this.$t('menus.createUser.passwordInvalidChars');
          }

          return true;
        }.bind(this),
      ],
      selectedNodeId: null,

      datepickerMenu: false,
      expiryDateValue: new Date(),
      expiryDateRules: [
        v => !!v || this.$t('menus.menuUploadLink.expiryDateRequired')
      ],

      linkNotes: "",
      maxfilesize: '5',
      maxfilesizeRules: [
        function(v) {
          if (v === "" || v === null) return true;

          let numV = parseFloat(v);

          if (isNaN(numV)) return self.$t('menus.menuUploadLink.invalidFilesize');
          if (numV <= 0) return self.$t('menus.menuUploadLink.filesizeMustBeGreater');

          if ( self.maxfilesizeunit === "GB" && numV > 500)
            return self.$t('menus.menuUploadLink.filesizeCannotBeGreater');

          if (self.maxfilesizeunit === "MB" && numV > 500000)
            return self.$t('menus.menuUploadLink.filesizeCannotBeGreater');

          return true;
        }
      ],

      maxfilesizeunit: "GB",
      availableFilesizeUnits: ["MB", "GB"],

      teamOnly: false,
      includeQrCode: false,

      embeddable: false,
      embeddableRules: [
        function(v) {
          if (self.password && v) return self.$t('menus.menuUploadLink.cannotCreateEmbeddable');
          return true;
        }
      ],

      addEmailInput: "",
      addEmailInputRules: Helper.getEmailValidationRules(this, { required: false }),

      emailRecipientsList: [],
      emailNote: '',

      errorMessage: "",
      successMessage: "",

      updatedLink: null,
    };
  },
  mounted() {
    this.resetForm();
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.resetForm();
      }
    },
    updatedLink(newValue) {
      if (newValue) {
        const cardText = this.$refs.cardText.$el;

        this.$nextTick(() => {
          cardText.scroll({
            top: cardText.scrollHeight,
            behavior: 'smooth',
          });
        });
      }
    }
  },
  methods: {
    resetForm() {
      this.tab = 0;
      const tenantExpiryDays = this.getTenantLinksExpiryDays;
      this.expiryDate = formatDate(addDays(new UTCDate(), tenantExpiryDays));

      if (this.currentNode && this.currentNode.id) {
        this.selectedNodeId = this.currentNode.id;
      }

      this.password = "";
      this.datepickerMenu = false;

      this.linkNotes = "";
      this.maxfilesize = '5'; // The dafault value is 5 GB
      this.maxfilesizeunit = this.availableFilesizeUnits[1];

      this.teamOnly = false;
      this.embeddable = false;
      this.includeQrCode = false;
      this.addEmailInput = "";
      this.emailRecipientsList = [];
      this.emailNote = '';

      this.errorMessage = "";
      this.successMessage = "";

      this.updatedLink = null;
    },
    async submitForm() {
      this.errorMessage = "";
      this.successMessage = "";

      const validForm = await this.$refs.form.validate();
      if (!validForm.valid) {
        return;
      }

      const nodeId = this.selectedNodeId || this.getNodeID;

      // convert to bytes
      let maxfilesize = parseFloat(this.maxfilesize) * 1000000;
      if (this.maxfilesizeunit === 'GB') {
        maxfilesize *= 1000;
      }

      const payload = {
        formData: {
          password: this.password,
          note: this.linkNotes,
          maxfilesize: maxfilesize,
          maxfilesizeunit: this.maxfilesizeunit,
          // send the date in UTC
          expireat: formatDateTimeMilliseconds(set(new UTCDate(this.expiryDate), {
            hours: 23,
            minutes: 59,
            seconds: 59,
            milliseconds: 0,
          })),
          nodeid: nodeId,
          teamonly: this.teamOnly,
          embeddable: this.embeddable ? 1 : 0,
        },
      };

      const self = this;

      const uploadLinkStore = useUploadLinkStore();
      let VERB = uploadLinkStore.createUploadLink;

      if (this.uploadlink) {
        payload.id = this.uploadlink.id;
        VERB = uploadLinkStore.updateUploadLink;
      }

      VERB(payload)
        .then(function(uploadlink) {
          if (self.emailRecipientsList.length > 0) {
            self.sendInvitations(uploadlink)
              .then((links) => {
                self.updatedLink = links.find((x) => x.id === uploadlink.id);

                const uploadLinkStore = useUploadLinkStore();
                uploadLinkStore.updateUploadlinks(links);
              });
          } else {
            self.updatedLink = uploadlink;
            const uploadLinkStore = useUploadLinkStore();
            uploadLinkStore.refreshUploadLinks(self.getNodeID);
          }

          self.successMessage = self.$t('menus.menuUploadLink.linkCreated');
        })
        .catch(function(error) {
          if (error?.response?.data?.error) {
            self.errorMessage = self.$localizeMessage(error.response.data.error);
          } else {
            self.errorMessage = self.$t('backend.system-error');
            console.log(error?.response?.data); // unhandled error
          }
        });
    },
    generatePassword() {
      this.password = Helper.generatePassword(10);
    },
    async addEmailRecipient() {
      const validForm = await this.$refs.emailsForm.validate();
      if (!validForm.valid) {
        return;
      }

      if (this.emailRecipientsList.indexOf(this.addEmailInput) >= 0) {
        this.addEmailInput = "";
        return;
      }

      this.emailRecipientsList.push(this.addEmailInput);
      this.addEmailInput = "";
    },
    removeEmailRecipient(email) {
      this.emailRecipientsList = this.emailRecipientsList.filter(x => x !== email);
    },
    sendInvitations(uploadlink) {
      this.errorMessage = '';
      const self = this;

      const payload = {
        linkId: uploadlink.id,
        receiver: this.emailRecipientsList.join(','),
        note: this.emailNote,
        includeqrcode: this.includeQrCode ? 1 : 0,
      };

      const uploadLinkStore = useUploadLinkStore();
      return uploadLinkStore.inviteUploadlink(payload)
        .then(function(response) {
          self.emailRecipientsList = [];
          self.emailNote = '';

          return Array.isArray(response?.links) ? response.links : [];
        });
    },
    copyUrlToClipboard() {
      if (!navigator?.clipboard?.writeText) {
        this.$showMessage(this.$t('menus.menuUploadLink.notCopiedToClipboard'), { type: 'error' });
        return;
      }

      navigator.clipboard.writeText(this.getUploadlinkURL).then(() => {
        this.$showMessage(this.$t('menus.menuUploadLink.copiedToClipboard'));
      }).catch((err) => {
        console.error(err);
        this.$showMessage(this.$t('menus.menuUploadLink.notCopiedToClipboard'), { type: 'error' });
      });
    },
    copyEmbeddedCodeToClipboard() {
      if (!navigator?.clipboard?.writeText) {
        this.$showMessage(this.$t('menus.menuUploadLink.notCopiedToClipboard'), { type: 'error' });
        return;
      }

      navigator.clipboard.writeText(this.getEmbedlinkCode).then(() => {
        this.$showMessage(this.$t('menus.menuUploadLink.copiedToClipboard'));
      }).catch((err) => {
        console.error(err);
        this.$showMessage(this.$t('menus.menuUploadLink.notCopiedToClipboard'), { type: 'error' });
      });
    },
    copyPasswordToClipboard() {
      if (this.password.length > 0) {
        navigator.clipboard.writeText(this.password);
      }
    },
  },
  computed: {
    expiryDate: {
      get: function () {
        return formatDate(this.expiryDateValue);
      },
      set: function (value) {
        this.expiryDateValue = value;
      }
    },
    getMinDatepicker() {
      return formatDate(new Date());
    },
    getTenantLinksExpiryDays() {
      const tenantStore = useTenantStore();
      return tenantStore.getTenantLinksExpiryDays;
    },
    currentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    getAccessPoints() {
      const nodeStore = useNodeStore();
      return nodeStore.getAccessPoints;
    },
    getNodeID() {
      if (this.currentNode && this.currentNode.id) {
        return this.currentNode.id;
      } else if (this.getAccessPoints && this.getAccessPoints.length > 0) {
        return this.getAccessPoints[0].id;
      }

      return false;
    },
    isFormDisabled() {
      return !this.currentNode || Boolean(this.updatedLink);
    },
    getUploadlinkURL() {
      if (this.updatedLink && this.updatedLink.url) {
        return window.location.origin + this.updatedLink.url;
      }
      return '';
    },
    getEmbedlinkCode() {
      if (this.updatedLink && this.updatedLink.code) {
        const code = `
          <iframe
            src="${window.location.origin}/embedded-upload/${this.updatedLink.code}"
            title="twUploadFrame"
            width="420px"
            height="400px"
            style="border: none; overflow: auto;"
          ></iframe>
        `;
        return code.replace(/\s+/g, ' ').trim(); // Replace multiple whitespaces with a single space and trim any leading/trailing spaces
      }
      return '';
    },
    isTeamMemberUser() {
      return this.$canControlTenantConfig() || this.$canBelongstoTeam();
    },
    canAccessClipboard() {
      // Note: this works only on localhost and HTTPS
      return window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function';
    }
  }
};
</script>
